import { makeAutoObservable, observable } from "mobx"
import React from "react"

import { t } from "@lingui/macro"

import { AdminService } from "src/api"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { createLoadingKeys } from "src/lib/loading"
import { reportSuccess, reportUnhandledApiError } from "src/lib/report"
import { IProperty } from "src/views/client-management/property/store"

export interface ICopyPropertySettingsPayload {
    integrationActive: boolean
}

export class CopyPropertySettingsStore {
    static Context = React.createContext<CopyPropertySettingsStore | null>(null)
    static LoadingKeys = createLoadingKeys("submit")

    properties: IProperty[] = []
    private payload: ICopyPropertySettingsPayload | null = null
    private selectedForCopy = observable.set<number>()

    // Progress tracking
    progress = 0
    totalToUpdate = 0

    constructor() {
        makeAutoObservable(this)
    }

    init(properties: IProperty[], payload: ICopyPropertySettingsPayload) {
        this.properties = properties
        this.payload = payload
        this.selectedForCopy.clear()
    }

    get allSelected() {
        if (this.properties.length === 0) {
            return false
        }

        return this.properties.every((property) =>
            this.selectedForCopy.has(property.propertyId),
        )
    }

    setAllSelected(checked: boolean) {
        if (checked) {
            this.properties.forEach((property) =>
                this.selectedForCopy.add(property.propertyId),
            )
        } else {
            this.selectedForCopy.clear()
        }
    }

    toggleCopy(property: IProperty) {
        const id = property.propertyId
        if (this.selectedForCopy.has(id)) {
            this.selectedForCopy.delete(id)
        } else {
            this.selectedForCopy.add(id)
        }
    }

    willCopy(property: IProperty) {
        return this.selectedForCopy.has(property.propertyId)
    }

    hasSelected() {
        return this.selectedForCopy.size > 0
    }

    selected() {
        return Array.from(this.selectedForCopy)
    }

    get progressPercentage() {
        if (this.totalToUpdate === 0) return 0
        return Math.round((this.progress / this.totalToUpdate) * 100)
    }

    get isSubmitting() {
        return this.progress > 0 && this.progress < this.totalToUpdate
    }

    @loads(() => CopyPropertySettingsStore.LoadingKeys.submit)
    async submit() {
        if (this.selectedForCopy.size === 0 || this.payload == null) {
            return
        }

        const ids = this.selected()

        const payload = this.payload
        const integrationActive = payload.integrationActive ?? false

        // Initialize progress tracking
        this.progress = 0
        this.totalToUpdate = ids.length

        try {
            // Rate limit: 5 requests per second to avoid overwhelming the server/browser
            const CHUNK_SIZE = 5
            const DELAY_BETWEEN_CHUNKS_MS = 1000 // 1 second delay = 5 requests per second

            const chunks: number[][] = []
            for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
                chunks.push(ids.slice(i, i + CHUNK_SIZE))
            }

            // Process each chunk with a delay between chunks
            for (let i = 0; i < chunks.length; i++) {
                const chunk = chunks[i]

                await Promise.all(
                    chunk.map((id) =>
                        AdminService.patchV1AdminProperty({
                            propertyId: id,
                            request: {
                                integration_active: integrationActive,
                            },
                        }),
                    ),
                )

                // Update progress
                this.progress += chunk.length

                // Add delay between chunks (except after the last chunk)
                if (i < chunks.length - 1) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, DELAY_BETWEEN_CHUNKS_MS),
                    )
                }
            }

            // Send a single channel event to trigger refresh
            // (instead of one per property to avoid overwhelming the system)
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "properties",
                    action: "update",
                    item: {
                        id: ids[0],
                        name: "",
                    },
                },
            })

            reportSuccess(t`copy-property-settings-modal.copy-success-message`)
        } catch (e) {
            reportUnhandledApiError(e)
            throw e
        }
    }
}
